import React, { useRef, useState } from "react";
import {
  Alert,
  Button,
  Modal,
  Progress,
  Upload,
  notification,
} from "antd";
import {
  DownloadOutlined,
  InboxOutlined,
} from "@ant-design/icons";

import { useAuth } from "../../context/auth";

// Matches bulkActiveChunkLimit on the API.
const CHUNK_SIZE = 100;
const FILE_LIMIT = 5 * 1024 * 1024;
const FILE_ACCEPT = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

const ACTION_LABEL = {
  active: "Active",
  inactive: "Inactive",
  delete: "Delete",
};
function formatAction(action) {
  return ACTION_LABEL[action] ?? action;
}

const BulkActiveModal = ({ visible, onCancel, onOk }) => {
  const { accessToken } = useAuth();

  // "idle" -> "parsed" -> "running" -> "done"
  const [stage, setStage] = useState("idle");
  const [parsing, setParsing] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [items, setItems] = useState([]);
  const [invalids, setInvalids] = useState([]);
  const [results, setResults] = useState([]);
  const [processed, setProcessed] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [stopped, setStopped] = useState(false);

  // A ref so the running loop sees the flag without being restarted.
  const cancelled = useRef(false);

  // Skipped rows never reach the API but still count towards the file's progress.
  const total = items.length + invalids.length;
  const failed = results.filter((result) => result.result === "Failed").length;
  const deleteCount = items.filter((item) => item.action === "delete").length;
  const succeeded = results.length - failed;

  function reset() {
    setStage("idle");
    setParsing(false);
    setFileName(null);
    setItems([]);
    setInvalids([]);
    setResults([]);
    setProcessed(0);
    setStopped(false);
    cancelled.current = false;
  }

  async function describeError(res) {
    try {
      const { message } = await res.json();
      return message;
    } catch {
      return res.status;
    }
  }

  async function downloadTemplate() {
    const url = new URL("/api/user/bulk-active", window.location);
    url.searchParams.append("mode", "template");

    const res = await fetch(url, {
      headers: { "X-ACCESS-TOKEN": accessToken },
    });
    if (!res.ok) {
      notification["error"]({
        message: "Something went wrong !",
        description: `Please re-login or contact web admin. (Code: ${await describeError(res)})`,
      });
      return;
    }

    const a = document.createElement("a");
    a.href = URL.createObjectURL(await res.blob());
    a.download = "Bulk-Active-Template.xlsx";
    a.click();
  }

  async function parseFile(file) {
    if (file.size > FILE_LIMIT) {
      notification["error"]({ message: "File must not exceed 5 MB" });
      return;
    }

    setParsing(true);

    const formData = new FormData();
    formData.append("file", file);

    const url = new URL("/api/user/bulk-active", window.location);
    url.searchParams.append("mode", "parse");

    const res = await fetch(url, {
      method: "POST",
      headers: { "X-ACCESS-TOKEN": accessToken },
      body: formData,
    });

    if (res.ok) {
      const { items, invalids } = await res.json();

      setFileName(file.name);
      setItems(items);
      setInvalids(invalids);
      setResults([]);
      setProcessed(0);
      setStage("parsed");
    } else {
      const message = await describeError(res);
      notification["error"]({
        message: "Cannot read this file",
        description: {
          file: "The file is not a readable .xlsx / .xls spreadsheet.",
          empty: "The file has no data rows.",
          limit: "The file has too many rows (max 20,000).",
        }[message] ?? `Please re-login or contact web admin. (Code: ${message})`,
      });
    }

    setParsing(false);
  }

  async function start() {
    cancelled.current = false;
    setStage("running");

    // Rows that never reach the API still belong in the report.
    const collected = invalids.map((invalid) => ({
      email: invalid.email,
      tel: invalid.tel,
      status: invalid.status,
      result: invalid.result,
      reason: invalid.reason,
    }));
    setResults(collected);
    setProcessed(invalids.length);

    const url = new URL("/api/user/bulk-active", window.location);

    let i = 0;
    try {
      for (; i < items.length; i += CHUNK_SIZE) {
        if (cancelled.current) break;

        const chunk = items.slice(i, i + CHUNK_SIZE);

        try {
          const res = await fetch(url, {
            method: "POST",
            headers: {
              "X-ACCESS-TOKEN": accessToken,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              items: chunk.map((item) => ({ email: item.email, tel: item.tel, action: item.action })),
            }),
          });
          if (!res.ok) throw new Error(await describeError(res));

          const { results } = await res.json();
          collected.push(...results);
        } catch (err) {
          // One bad chunk must not abort the rest of the file.
          collected.push(...chunk.map((item) => ({
            email: item.email,
            tel: item.tel,
            status: formatAction(item.action),
            result: "Failed",
            reason: `Request failed (${err.message})`,
          })));
        }

        setProcessed(invalids.length + Math.min(i + CHUNK_SIZE, items.length));
        setResults([...collected]);
      }
    } finally {
      // Stopping leaves the tail untouched - say so rather than omitting the rows.
      const remaining = items.slice(i);
      if (remaining.length > 0) {
        collected.push(...remaining.map((item) => ({
          email: item.email,
          tel: item.tel,
          status: formatAction(item.action),
          result: "Failed",
          reason: "Stopped before this row was processed",
        })));
        setResults([...collected]);
        setStopped(true);
      }

      // Always leave the modal in a closable state, even if something threw.
      setStage("done");
      onOk();
    }
  }

  async function downloadReport() {
    setDownloading(true);

    const url = new URL("/api/user/bulk-active", window.location);
    url.searchParams.append("mode", "xlsx");

    const res = await fetch(url, {
      method: "POST",
      headers: {
        "X-ACCESS-TOKEN": accessToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ results }),
    });

    if (res.ok) {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await res.blob());
      a.download = `Bulk-Active-Result_${new Date().toISOString()}.xlsx`;
      a.click();
    } else {
      notification["error"]({
        message: "Something went wrong !",
        description: `Please re-login or contact web admin. (Code: ${await describeError(res)})`,
      });
    }

    setDownloading(false);
  }

  const running = stage === "running";
  const percent = total === 0 ? 100 : Math.round((processed / total) * 100);

  return (
    <Modal
      title="Bulk Active / Inactive"
      visible={visible}
      maskClosable={!running}
      closable={!running}
      onCancel={() => {
        if (running) return;
        reset();
        onCancel();
      }}
      footer={[
        running ? (
          <Button
            key="stop"
            danger
            shape="round"
            onClick={() => { cancelled.current = true; }}
          >
            Stop
          </Button>
        ) : (
          <Button
            key="close"
            shape="round"
            onClick={() => {
              reset();
              onCancel();
            }}
          >
            Close
          </Button>
        ),
        stage === "done" ? (
          <Button
            key="report"
            type="primary"
            shape="round"
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={downloadReport}
          >
            Download Result
          </Button>
        ) : (
          <Button
            key="start"
            type="primary"
            shape="round"
            loading={running}
            disabled={stage !== "parsed" || (items.length === 0 && invalids.length === 0)}
            onClick={() => {
              // Deletion is permanent, so make the operator say it twice.
              if (deleteCount === 0) return start();

              Modal.confirm({
                title: `Permanently delete ${deleteCount} account(s)?`,
                content: "Accounts with usage history are refused. The rest cannot be recovered.",
                okText: "Delete",
                okButtonProps: { danger: true },
                // Not `onOk: start` - antd would keep the mask up for the whole
                // run, hiding the progress bar and blocking Stop.
                onOk: () => { start(); },
              });
            }}
          >
            Start
          </Button>
        ),
      ]}
    >
      {stage === "idle" && (
        <>
          <Alert
            className="mb-4"
            type="info"
            showIcon
            message="File format"
            description={
              <div>
                <div>A = <b>Email</b>, B = <b>Tel</b>, C = <b>Status</b>: Active / Inactive / Delete.</div>
                <div>Email and Tel must both match the account, or the row is skipped.</div>
                <div><b>Delete</b> is permanent, and only with no usage history.</div>
                <div>Header optional. Max 20,000 rows / 5 MB.</div>
                <Button className="px-0" type="link" onClick={downloadTemplate}>
                  Download template
                </Button>
              </div>
            }
          />

          <Upload.Dragger
            accept=".xlsx,.xls"
            maxCount={1}
            showUploadList={false}
            disabled={parsing}
            beforeUpload={(file) => {
              // The API parses the sheet, so keep antd from uploading on its own.
              if (FILE_ACCEPT.includes(file.type) || /\.xlsx?$/i.test(file.name)) parseFile(file);
              else notification["error"]({ message: "Please select an .xlsx or .xls file" });
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {parsing ? "Reading file..." : "Click or drag an Excel file to this area"}
            </p>
          </Upload.Dragger>
        </>
      )}

      {stage !== "idle" && (
        <>
          <div className="mb-2">
            File: <b>{fileName}</b>
          </div>
          <div className="mb-2">
            Ready to process: <b>{items.length}</b> of <b>{total}</b> row{total === 1 ? "" : "s"}
          </div>

          {deleteCount > 0 && stage === "parsed" && (
            <Alert
              className="mb-4"
              type="error"
              showIcon
              message={`${deleteCount} row(s) will be deleted permanently`}
              description="Accounts with usage history are refused and reported. Cannot be undone."
            />
          )}

          {invalids.length > 0 && (
            <Alert
              className="mb-4"
              type="warning"
              showIcon
              message={`${invalids.length} row(s) will be skipped`}
              description={
                <div style={{ maxHeight: 120, overflowY: "auto" }}>
                  {invalids.slice(0, 20).map((invalid) => (
                    <div key={invalid.row}>
                      Row {invalid.row}: {invalid.email || "(empty)"} / {invalid.tel || "(empty)"} - {invalid.reason}
                    </div>
                  ))}
                  {invalids.length > 20 && <div>...and {invalids.length - 20} more (see the result file)</div>}
                </div>
              }
            />
          )}

          {stage !== "parsed" && (
            <>
              <Progress
                percent={percent}
                status={running ? "active" : stopped ? "exception" : "success"}
              />
              <div className="mt-2">
                Processed <b>{processed}</b> / <b>{total}</b>
                {" | "}
                <span style={{ color: "#55B491" }}>Success {succeeded}</span>
                {" / "}
                <span style={{ color: "#EB5757" }}>Failed {failed}</span>
              </div>
            </>
          )}

          {stage === "done" && (
            <Alert
              className="mt-4"
              type={failed > 0 ? "warning" : "success"}
              showIcon
              message={stopped ? "Stopped" : "Finished"}
              description="Download the result file for per-row detail."
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default BulkActiveModal;
